import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useRootSelector, useRootDispatch } from "store/hooks";
import { Cockpit } from "components/layout/cockpit/Cockpit";
import { useAssets } from "hooks/useAssets";
import { useAuth0 } from "@auth0/auth0-react";
import { resetUser } from "store/user/user-slice";
import { resetAccounts } from "store/account/account-slice";
import { resetInfo } from "store/payments/payments-slice";
import { clearAllNotifications } from "store/notification/notification-slice";
import {
  FT_TOKEN_KEY,
  SESSION_EXPIRED_KEY,
  SESSION_EXPIRED_REASON_INACTIVE,
} from "constants/session";

export const App = () => {
  const { createAssetUrl } = useAssets();
  const [showApp, setshowApp] = useState(false);
  const { logout } = useAuth0();
  const dispatch = useRootDispatch();
  const { client, applicationName } = useRootSelector((state) => state.config);
  const navigate = useNavigate();

  useEffect(() => {
    const appHeight = () => {
      document.documentElement.style.setProperty(
        "--appHeight",
        `${window.innerHeight}px`,
      );
    };
    window.addEventListener("resize", appHeight);
    appHeight();
  }, []);

  useEffect(() => {
    if (!showApp) {
      const envBaseUrl = import.meta.env.REACT_APP_FT_REQUEST_BASE_URL;

      const faviconUrl = createAssetUrl("favicon");

      const manifestUrl = `${envBaseUrl}clients/${
        client.customerKey
      }/config/manifest.json?productKey=payment_center&idpTenantId=${encodeURIComponent(
        import.meta.env.REACT_APP_ASSETS_TENENT as string,
      )}`;
      const themeUrl = !!client.idPId
        ? `${envBaseUrl}themes/PAYMENT_CENTER/theme.css?applicationClientId=${client.idPId}`
        : `${envBaseUrl}themes/PAYMENT_CENTER/theme.css?customerKey=${client.customerKey}`;

      const faviconEle = document.createElement("link");
      faviconEle.setAttribute("rel", "icon");
      faviconEle.setAttribute("href", faviconUrl);

      const manifestEle = document.createElement("link");
      manifestEle.setAttribute("rel", "manifest");
      manifestEle.setAttribute("href", manifestUrl);

      const themeStyleEle = document.createElement("link");
      themeStyleEle.setAttribute("rel", "stylesheet");
      themeStyleEle.setAttribute("href", themeUrl);
      themeStyleEle.onload = () => {
        setshowApp(true);
      };

      document.head.appendChild(faviconEle);
      document.head.appendChild(manifestEle);
      document.head.appendChild(themeStyleEle);
    }
  }, [client.idPId, client.customerKey, createAssetUrl, showApp]);

  useEffect(() => {
    document.title = applicationName ? applicationName : "";

    if (!!client.clientId && !!(window as any).dtrum) {
      (window as any).dtrum.sendSessionProperties({
        clientId: client.clientId,
      });
    }
  }, [client, applicationName]);

  const markSessionExpired = () => {
    sessionStorage.setItem(
      SESSION_EXPIRED_KEY,
      SESSION_EXPIRED_REASON_INACTIVE,
    );
  };

  const handleAuthenticatedIdleLogout = () => {
    markSessionExpired();
    dispatch(resetUser());
    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/${client.customerKey}`,
      },
    });
  };

  const handleQuickPayIdleLogout = () => {
    markSessionExpired();
    sessionStorage.removeItem(FT_TOKEN_KEY);
    dispatch(clearAllNotifications());
    navigate(`/${client.customerKey}`, { replace: true });
    dispatch(resetAccounts());
    dispatch(resetInfo());
  };

  return showApp ? (
    <Cockpit
      onAuthenticatedIdleLogout={handleAuthenticatedIdleLogout}
      onQuickPayIdleLogout={handleQuickPayIdleLogout}
    />
  ) : (
    <ft-screen-loader></ft-screen-loader>
  );
};
